import type { TaskTiming } from '@core/ui/TaskTiming';

/**
 * Представления списка задач.
 *
 * Три вместо шести из «Структуры» §6.1 — решение владельца от 30.08.2026. Просроченное
 * не прячется за отдельный фильтр, а попадает в «Сегодня»: то, что горит, юрист должен
 * видеть сразу при открытии экрана, а не искать в другой вкладке.
 */
export type TasksTab = 'today' | 'tomorrow' | 'upcoming';

export const TABS: { key: TasksTab; label: string }[] = [
  { key: 'today', label: 'Сегодня' },
  { key: 'tomorrow', label: 'Завтра' },
  { key: 'upcoming', label: 'Ближайшие' },
];

export interface TaskListItem {
  id: string;
  title: string;
  /** «Просрочено — до 27 августа» либо «Сегодня, 15:00». */
  timing: string;
  timingAccent: TaskTiming;
  /** Дело, к которому относится задача; нажатие ведёт в его карточку. */
  caseId: string | null;
  caseTitle: string | null;
  isCompleted: boolean;
  /** Сам срок, а не его подпись: с ним открывается диалог переноса. */
  deadlineAt: number;
}

/**
 * Строка списка: заголовок группы либо задача.
 *
 * `muted` — заглядывание вперёд: строка относится не к выбранной вкладке, а к дням
 * за ней, и набрана приглушённо. Просьба юриста после первого дня работы: «удобно,
 * чтобы под сегодняшними висели серым задачи ближайших дней».
 */
export type TasksListEntry =
  | { kind: 'header'; title: string; count: number; isOverdue: boolean; muted?: boolean }
  | { kind: 'item'; task: TaskListItem; muted?: boolean };

/** Дело, к которому можно привязать задачу. */
export interface TaskCaseOption {
  id: string;
  title: string;
}

export interface TasksState {
  isLoading: boolean;
  tab: TasksTab;
  entries: TasksListEntry[];
  /**
   * Дела, к которым можно привязать новую задачу.
   *
   * Привязка предлагается, но не требуется: юрист вправе поставить себе напоминание,
   * не относящееся ни к какому делу. Решение владельца от 30.08.2026.
   */
  cases: TaskCaseOption[];
  overdueCount: number;
}

export const tasksOf = (entries: TasksListEntry[]): TaskListItem[] =>
  entries.filter((entry) => entry.kind === 'item').map((entry) => entry.task);

/**
 * Есть ли на вкладке хоть что-то своё, а не подсмотренное вперёд.
 *
 * От этого зависит пустое состояние: «На сегодня задач нет» надо сказать и тогда,
 * когда ниже висят серым завтрашние — иначе они читаются как сегодняшние.
 */
export const isEmptyTab = (entries: TasksListEntry[]): boolean =>
  entries.every((entry) => entry.muted === true);
